<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { getTextVariant } from '@/utils/stringUtils';
import { PACKAGE, HOTEL_ONLY, AIR_ONLY, MDT, CRUISE } from '@/constants';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import { useAdminSetupStore } from '@/store/AdminSetupStore';
import { useGroupTypeStore } from '@/store/GroupTypeStore';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import CalendarSetupModal from '@/components/modal/CalendarSetupModal.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const adminSetupStore = useAdminSetupStore();
const groupTypeManagement = useGroupTypeStore();
const isLoading = ref(false);
const dialog = useDialog();
const currentLanguage = getCurrentLocale();
const initGroupType = ref([
  {
    limitDate: 0,
    groupList: []
  }
]);
const initData = ref({
  packageDeparture: {
    startDateConfig: initGroupType,
    endDateConfig: initGroupType,
    quoteType: PACKAGE
  },
  hotelDeparture: {
    startDateConfig: initGroupType,
    endDateConfig: initGroupType,
    quoteType: HOTEL_ONLY
  },
  airDeparture: {
    startDateConfig: initGroupType,
    endDateConfig: initGroupType,
    quoteType: AIR_ONLY
  },
  cruiseDeparture: {
    startDateConfig: initGroupType,
    endDateConfig: initGroupType,
    quoteType: CRUISE
  },
  tourDeparture: {
    startDateConfig: initGroupType,
    endDateConfig: initGroupType,
    quoteType: MDT
  }
});

const calendarConfigData = ref({});
const listGroupTypes = ref([]);

const getgroupTypeData = () => {
  isLoading.value = true;
  groupTypeManagement
    .getGroupTypeList()
    .then((res: any) => {
      listGroupTypes.value = res.data.filter((item: any) => item.status !== 'CLOSED');
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => (isLoading.value = false));
};

const getDepartureData = () => {
  isLoading.value = true;
  adminSetupStore
    .getDepartureConfig()
    .then((res: any) => {
      if (res) {
        calendarConfigData.value = res;
      } else {
        calendarConfigData.value = initData.value;
      }
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => (isLoading.value = false));
};

const getQuoteTitles = (title: string) => {
  switch (title) {
    case AIR_ONLY:
      return t('management.email.air_quote');
    case CRUISE:
      return t('management.email.cruise_quote');
    case HOTEL_ONLY:
      return t('management.email.hotel_quote');
    case MDT:
      return t('management.email.tour_quote');
    default:
      return t('management.email.package_quote');
  }
};

const getEditModalTitles = (title: string, isStart: boolean) => {
  const afterTitle = isStart ? t('config_items.add_calendar_start') : t('config_items.add_calendar_end');
  switch (title) {
    case AIR_ONLY:
      return t('config_items.air') + ' - ' + afterTitle;
    case CRUISE:
      return t('config_items.cruises') + ' - ' + afterTitle;
    case HOTEL_ONLY:
      return t('config_items.hotel') + ' - ' + afterTitle;
    case MDT:
      return t('config_items.tour') + ' - ' + afterTitle;
    default:
      return t('config_items.packages') + ' - ' + afterTitle;
  }
};

const getNamesString = (arr: Array<any>) => {
  if (arr.length) {
    return listGroupTypes.value
      .filter((item) => arr.includes(item.sk))
      .map((item) => getTextVariant(currentLanguage, item.name, item.nameFr))
      .join(', ');
  } else {
    return '';
  }
};

const handleUpdateConfig = (saveRules: any, title: string, isStart: boolean) => {
  isLoading.value = true;
  const payload = {
    type: title,
    value: {}
  };
  Object.values(calendarConfigData.value).forEach((item: any) => {
    if (item.quoteType == title) {
      if (isStart) {
        item.startDateConfig = saveRules;
      } else {
        item.endDateConfig = saveRules;
      }
      payload.value = item;
    }
  });

  adminSetupStore
    .updateDepartureConfig(payload)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    })
    .finally(() => (isLoading.value = false));
};

const handleOpenModal = (title: string, groupTypeData: any, isStart: boolean, dateData: any) => {
  const dialogRef = dialog.open(CalendarSetupModal, {
    props: {
      header: getEditModalTitles(title, isStart),
      modal: true,
      style: {
        width: '55vw'
      }
    },
    data: {
      groupTypeData: groupTypeData,
      isStart: isStart,
      listGroupTypes: listGroupTypes,
      dateData: dateData
    },
    emits: {
      isSave: (saveRules: any) => {
        handleUpdateConfig(saveRules, title, isStart);
        dialogRef.close();
      }
    }
  });
};

onMounted(() => {
  getgroupTypeData();
  getDepartureData();
});
</script>
<template>
  <div class="calendar-config">
    <LoadingSpinner v-if="isLoading" />
    <div v-else class="card">
      <h4>{{ $t('config_items.calendar_config') }}</h4>
      <div class="calendar-detail">
        <div class="edit-field">
          <h5 class="config-title">{{ $t('config_items.calendar_setup_start') }}</h5>
          <div v-for="(quote, index) in calendarConfigData" :key="index" class="flex align-items-start">
            <div class="quote-title mr-3 mt-3 w-9rem">{{ getQuoteTitles(quote.quoteType) }}</div>
            <div class="flex-column">
              <div v-for="(quoteItem, quoteIndex) in quote.startDateConfig" :key="quoteIndex" class="flex mb-3 list-quote-rule">
                <InputText disabled :modelValue="quoteItem.limit" class="mr-3 w-3"></InputText>
                <span v-tooltip.top="getNamesString(quoteItem.groupList)">
                  <MultiSelect
                    :maxSelectedLabels="2"
                    display="chip"
                    disabled
                    optionValue="sk"
                    :optionLabel="getTextVariant(currentLanguage, 'name', 'nameFr')"
                    v-model="quoteItem.groupList"
                    :options="listGroupTypes"
                    :placeholder="$t('advance_quote.form.select_group_type')"
                    class="multi-grouptype mr-3"
                /></span>
              </div>
            </div>
            <i class="pi edit-pi mt-3 pi-pencil cursor-pointer edit-icon" v-if="authorizations?.configurationPage?.configurable_items_edit" @click="handleOpenModal(quote.quoteType, quote.startDateConfig, true, quote.endDateConfig)"></i>
          </div>
        </div>
        <div class="seperator min-h-full"></div>
        <div class="edit-field">
          <h5 class="config-title">{{ $t('config_items.calendar_setup_end') }}</h5>
          <div v-for="(quote, index) in calendarConfigData" :key="index" class="flex align-items-start">
            <div class="quote-title mr-3 mt-3 w-9rem">{{ getQuoteTitles(quote.quoteType) }}</div>
            <div class="flex-column">
              <div v-for="(quoteItem, quoteIndex) in quote.endDateConfig" :key="quoteIndex" class="flex mb-3 list-quote-rule">
                <InputText disabled :modelValue="quoteItem.limit" class="mr-3 w-3"></InputText>
                <span v-tooltip.top="getNamesString(quoteItem.groupList)">
                  <MultiSelect
                    :maxSelectedLabels="2"
                    display="chip"
                    disabled
                    optionValue="sk"
                    :optionLabel="getTextVariant(currentLanguage, 'name', 'nameFr')"
                    v-model="quoteItem.groupList"
                    :options="listGroupTypes"
                    :placeholder="$t('advance_quote.form.select_group_type')"
                    class="multi-grouptype mr-3"
                  />
                </span>
              </div>
            </div>
            <i class="pi edit-pi mt-3 pi-pencil cursor-pointer edit-icon" v-if="authorizations?.configurationPage?.configurable_items_edit" @click="handleOpenModal(quote.quoteType, quote.endDateConfig, false, quote.startDateConfig)"></i>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.calendar-config {
  .calendar-detail {
    display: flex;
    justify-content: space-between;
    margin-top: 2rem;
    flex-wrap: wrap;
    .edit-field {
      .p-inputtext {
        width: 7rem;
      }
      .multi-grouptype {
        max-width: 25rem;
        min-width: 25rem;
        .list-quote-rule {
          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }
  }
  .seperator {
    width: 1px;
    background: $col-whiteblu;
  }
  .p-disabled,
  .p-component:disabled {
    opacity: 1;
  }
}
</style>
